/**
 * 🏢 FLEET OWNERS / HOSTS MANAGEMENT
 * View all fleet owners/hosts and their vehicles grouped together
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Skeleton,
  Alert,
  Stack,
  Badge,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search,
  Person,
  DirectionsCar,
  Visibility,
  Email,
  Phone,
  CalendarToday,
  TrendingUp,
  Warning,
  CheckCircle,
  Refresh,
  Business,
  Star,
  LocalShipping,
  Collections,
  Power,
  Block,
  ReportProblem,
  DriveEta,
  CloudUpload,
  Delete,
  Add,
  SailingOutlined,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { vehicleApprovalService } from '@/services/vehicleApprovalService';
import { yachtApprovalService } from '@/services/yachtApprovalService';
import { VehicleImageManagementDialog } from '@/components/vehicles/VehicleImageManagementDialog';
import { YachtImageManagementDialog } from '@/components/yachts/YachtImageManagementDialog';
import {
  suspendVehicle,
  blacklistVehicle,
  requestVehicleReactivation,
} from '@/services/moderationService';
import { driverService } from '@/services/driverService';
import { adminVehicleService } from '@/services/adminVehicleService';
import { AddVehicleForHostDialog } from '@/components/vehicles/AddVehicleForHostDialog';
import { AddYachtForHostDialog } from '@/components/yachts/AddYachtForHostDialog';
import { getApiBaseUrl } from '@/config/environment';

interface HostVehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  plateNumber: string;
  status: string;
  vin?: string;
  isActive?: boolean;
  suspensionStatus?: 'active' | 'suspended' | 'blacklisted';
  suspensionReason?: string;
  canHostReactivate?: boolean;
  vehicleTypeId?: string;
  vehicleTypeName?: string;
  bodyTypeId?: string;
  bodyTypeName?: string;
}

interface HostDriver {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseNumber: string;
  yearsOfExperience: number;
  isVerified: boolean;
  status: string;
  profilePhotoUrl?: string;
  totalBookings: number;
}

interface HostYacht {
  id: string;
  name: string;
  manufacturer?: string;
  model?: string;
  year: number;
  yacht_type: string;
  length: number;
  registration_number?: string;
  status: string;
  isActive?: boolean;
  suspensionStatus?: 'active' | 'suspended' | 'blacklisted';
  guest_capacity?: number;
}

interface FleetOwner {
  id: string;
  name: string;
  type: 'individual' | 'fleet_manager' | 'concierge';
  status: 'approved' | 'pending' | 'rejected';
  email: string;
  phone?: string;
  avatar?: string;
  joinedDate?: string;
  totalVehicles: number;
  activeVehicles: number;
  pendingVehicles: number;
  approvedVehicles: number;
  rejectedVehicles: number;
  totalDrivers: number;
  verifiedDrivers: number;
  totalBookings: number;
  totalEarnings: number;
  complianceStatus?: 'compliant' | 'at_risk' | 'non_compliant' | null;
  complianceDeadline?: string | null;
  rating?: number;
  vehicles: HostVehicle[];
  drivers: HostDriver[];
  yachts: HostYacht[];
  totalYachts: number;
  activeYachts: number;
  pendingYachts: number;
  approvedYachts: number;
  // Company branding
  companyName?: string;
  companyLogoUrl?: string;
  companyLogoThumbnailUrl?: string;
  companyLogoUploadedAt?: string;
}

interface HostsOverview {
  overview: {
    totalOwners: number;
    individualHosts: number;
    fleetManagers: number;
    conciergeHosts: number;
  };
  byStatus: {
    approved: number;
    pending: number;
    rejected: number;
  };
  assets: {
    totalVehicles: number;
    approvedVehicles: number;
    totalDrivers: number;
    verifiedDrivers: number;
  };
  fleetCompliance?: {
    compliant: number;
    atRisk: number;
    nonCompliant: number;
  };
}

export const FleetOwnersPage: React.FC = () => {
  const [owners, setOwners] = useState<FleetOwner[]>([]);
  const [overview, setOverview] = useState<HostsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'individual' | 'fleet' | 'concierge'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending' | 'rejected'>('all');
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);

  // Derive selectedOwner from owners state to ensure it's always up-to-date
  const selectedOwner = selectedOwnerId ? owners.find(o => o.id === selectedOwnerId) || null : null;
  const [imageDialog, setImageDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<{ id: string; name: string } | null>(null);
  const [yachtImageDialog, setYachtImageDialog] = useState(false);
  const [selectedYacht, setSelectedYacht] = useState<{ id: string; name: string } | null>(null);
  const [loadingVehicles, setLoadingVehicles] = useState<Record<string, boolean>>({});
  const [loadingDrivers, setLoadingDrivers] = useState<Record<string, boolean>>({});
  const [loadingYachts, setLoadingYachts] = useState<Record<string, boolean>>({});

  // Filter states
  const [hasVehiclesFilter, setHasVehiclesFilter] = useState<boolean | null>(null);
  const [hasDriversFilter, setHasDriversFilter] = useState<boolean | null>(null);

  // Change Type dialog
  const [changeTypeDialog, setChangeTypeDialog] = useState<{
    open: boolean;
    hostId: string;
    hostName: string;
    currentType: FleetOwner['type'];
  }>({
    open: false,
    hostId: '',
    hostName: '',
    currentType: 'individual',
  });
  const [selectedNewType, setSelectedNewType] = useState<FleetOwner['type']>('individual');

  // Moderation dialogs
  const [moderationDialog, setModerationDialog] = useState<{
    open: boolean;
    action: 'suspend' | 'blacklist' | 'reactivate' | null;
    vehicleId: string;
    vehicleName: string;
    ownerId: string;
  }>({
    open: false,
    action: null,
    vehicleId: '',
    vehicleName: '',
    ownerId: '',
  });
  const [moderationReason, setModerationReason] = useState('');

  // Add Vehicle dialog state
  const [addVehicleDialog, setAddVehicleDialog] = useState<{
    open: boolean;
    hostId: string;
    hostName: string;
  }>({
    open: false,
    hostId: '',
    hostName: '',
  });

  // Add Yacht dialog state
  const [addYachtDialog, setAddYachtDialog] = useState<{
    open: boolean;
    hostId: string;
    hostName: string;
  }>({
    open: false,
    hostId: '',
    hostName: '',
  });

  // Company Branding state
  const [companyBrandingDialog, setCompanyBrandingDialog] = useState<{
    open: boolean;
    hostId: string;
    hostName: string;
    currentName?: string;
    currentLogoUrl?: string;
  }>({
    open: false,
    hostId: '',
    hostName: '',
  });
  const [companyName, setCompanyName] = useState('');
  const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Compress logo image (same as vehicle images)
  const compressLogoImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');

          // For logos, maintain square aspect ratio and resize to 800x800 max
          const maxSize = 800;
          let width = img.width;
          let height = img.height;

          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height * maxSize) / width;
              width = maxSize;
            } else {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Compress with quality adjustment to keep under 500KB
          let quality = 0.9;
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // If still too large, reduce quality
              if (blob.size > 500 * 1024 && quality > 0.5) {
                quality = 0.7;
                canvas.toBlob(
                  (secondBlob) => {
                    if (secondBlob) {
                      resolve(secondBlob);
                    } else {
                      resolve(blob);
                    }
                  },
                  'image/jpeg',
                  quality
                );
              } else {
                resolve(blob);
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    loadFleetOwners();
  }, [activeTab, statusFilter]);

  const loadOverview = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/hosts/overview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Overview API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Failed to load overview: ${response.statusText}`);
      }

      const data = await response.json();
      // API wraps response in {success: true, data: {...}}
      setOverview(data.data || data);
    } catch (err) {
      console.error('❌ Failed to load hosts overview:', err);
    }
  };

  const loadFleetOwners = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params for hosts
      const params = new URLSearchParams();
      const typeParam = activeTab === 'fleet' ? 'fleet' : activeTab === 'concierge' ? 'concierge' : activeTab === 'individual' ? 'individual' : 'all';
      params.append('type', typeParam);
      params.append('status', statusFilter);


      // Fetch BOTH hosts AND all vehicles in parallel for performance
      const [hostsResponse, approvedVehicles, pendingVehicles, rejectedVehicles] = await Promise.all([
        fetch(`${getApiBaseUrl()}/api/v1/admin/hosts?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }),
        vehicleApprovalService.getPendingVehicles({ status: 'approved', limit: 1000 }),
        vehicleApprovalService.getPendingVehicles({ status: 'pending_approval', limit: 1000 }),
        vehicleApprovalService.getPendingVehicles({ status: 'rejected', limit: 1000 })
      ]);

      if (!hostsResponse.ok) {
        const errorData = await hostsResponse.text();
        console.error('❌ Hosts API Error:', {
          status: hostsResponse.status,
          statusText: hostsResponse.statusText,
          error: errorData
        });
        throw new Error(`Failed to load hosts: ${hostsResponse.statusText}`);
      }

      const data = await hostsResponse.json();
      const hosts = data.data?.hosts || data.hosts || [];

      // Combine ALL vehicles from all statuses
      const allVehicles = [
        ...(approvedVehicles.success ? approvedVehicles.data.vehicles : []),
        ...(pendingVehicles.success ? pendingVehicles.data.vehicles : []),
        ...(rejectedVehicles.success ? rejectedVehicles.data.vehicles : [])
      ];

      // Create a map of hostId -> vehicles[] for quick lookup
      const vehiclesByHost = new Map<string, HostVehicle[]>();
      allVehicles.forEach((vehicle: any) => {
        const hostId = vehicle.host?.id;
        if (!hostId) return;

        if (!vehiclesByHost.has(hostId)) {
          vehiclesByHost.set(hostId, []);
        }

        vehiclesByHost.get(hostId)!.push({
          id: vehicle.id,
          make: vehicle.vehicle.make,
          model: vehicle.vehicle.model,
          year: vehicle.vehicle.year,
          plateNumber: vehicle.vehicle.plateNumber,
          status: vehicle.status,
          vin: vehicle.vehicle.vin,
          isActive: vehicle.isActive !== undefined ? vehicle.isActive : true,
          suspensionStatus: vehicle.suspensionStatus,
          suspensionReason: vehicle.suspensionReason,
          canHostReactivate: vehicle.canHostReactivate,
          vehicleTypeId: vehicle.vehicle.vehicleTypeId,
          vehicleTypeName: vehicle.vehicle.vehicleTypeName,
          bodyTypeId: vehicle.vehicle.bodyTypeId,
          bodyTypeName: vehicle.vehicle.bodyTypeName
        });
      });

      // Transform backend response and attach vehicles
      const transformedOwners: FleetOwner[] = hosts.map((host: any) => ({
        id: host.id,
        name: host.name,
        type: host.type,
        status: host.status,
        email: host.contact?.email || '',
        phone: host.contact?.phone || '',
        totalVehicles: host.assets?.vehiclesCount || 0,
        approvedVehicles: host.assets?.approvedVehicles || 0,
        activeVehicles: host.assets?.approvedVehicles || 0,
        pendingVehicles: (host.assets?.vehiclesCount || 0) - (host.assets?.approvedVehicles || 0),
        rejectedVehicles: 0,
        totalDrivers: host.assets?.driversCount || 0,
        verifiedDrivers: host.assets?.verifiedDrivers || 0,
        totalYachts: host.assets?.yachtsCount || 0,
        approvedYachts: host.assets?.approvedYachts || 0,
        activeYachts: host.assets?.approvedYachts || 0,
        pendingYachts: (host.assets?.yachtsCount || 0) - (host.assets?.approvedYachts || 0),
        totalBookings: host.performance?.totalBookings || 0,
        totalEarnings: host.performance?.totalEarnings || 0,
        complianceStatus: host.compliance?.status || null,
        complianceDeadline: host.compliance?.deadline || null,
        vehicles: vehiclesByHost.get(host.id) || [], // ✅ Attach vehicles immediately!
        drivers: [], // Drivers still loaded on-demand (less critical)
        yachts: [], // Yachts loaded on-demand when expanding row
        // Company branding (from backend nested branding object)
        companyName: host.branding?.companyName || host.company_name || host.companyName,
        companyLogoUrl: host.branding?.logoUrl || host.company_logo_url || host.companyLogoUrl,
        companyLogoThumbnailUrl: host.branding?.thumbnailUrl || host.company_logo_thumbnail_url || host.companyLogoThumbnailUrl,
        companyLogoUploadedAt: host.branding?.uploadedAt || host.company_logo_uploaded_at || host.companyLogoUploadedAt
      }));

      setOwners(transformedOwners);
    } catch (err) {
      console.error('❌ Failed to load fleet owners:', err);
      setError('Failed to load fleet owners data');
      toast.error('Failed to load fleet owners');
    } finally {
      setLoading(false);
    }
  };

  const filteredOwners = owners.filter(owner => {
    // Text search filter
    const matchesSearch = owner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      owner.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      owner.phone?.toLowerCase().includes(searchQuery.toLowerCase());

    // Has vehicles filter
    const matchesVehicles = hasVehiclesFilter === null ||
      (hasVehiclesFilter ? owner.totalVehicles > 0 : owner.totalVehicles === 0);

    // Has drivers filter
    const matchesDrivers = hasDriversFilter === null ||
      (hasDriversFilter ? owner.totalDrivers > 0 : owner.totalDrivers === 0);

    return matchesSearch && matchesVehicles && matchesDrivers;
  });

  const totalStats = React.useMemo(() => {
    if (!overview) {
      return {
        totalOwners: 0,
        individualHosts: 0,
        fleetManagers: 0,
        conciergeHosts: 0,
        totalVehicles: 0,
        approvedVehicles: 0,
        totalDrivers: 0,
        verifiedDrivers: 0,
        approvedHosts: 0,
        pendingHosts: 0,
        rejectedHosts: 0,
      };
    }

    return {
      totalOwners: overview.overview?.totalOwners || 0,
      individualHosts: overview.overview?.individualHosts || 0,
      fleetManagers: overview.overview?.fleetManagers || 0,
      conciergeHosts: overview.overview?.conciergeHosts || 0,
      totalVehicles: overview.assets?.totalVehicles || 0,
      approvedVehicles: overview.assets?.approvedVehicles || 0,
      totalDrivers: overview.assets?.totalDrivers || 0,
      verifiedDrivers: overview.assets?.verifiedDrivers || 0,
      approvedHosts: overview.byStatus?.approved || 0,
      pendingHosts: overview.byStatus?.pending || 0,
      rejectedHosts: overview.byStatus?.rejected || 0,
    };
  }, [overview]);

  /**
   * 🚗 Load vehicles for a specific host
   */
  const loadHostVehicles = async (hostId: string) => {
    // Skip if already loading
    if (loadingVehicles[hostId]) return;

    // Check if already loaded
    const owner = owners.find(o => o.id === hostId);
    if (owner && owner.vehicles && owner.vehicles.length > 0) return;

    setLoadingVehicles(prev => ({ ...prev, [hostId]: true }));

    try {

      // Fetch ALL vehicles and filter by host
      const [approvedRes, pendingRes, rejectedRes] = await Promise.all([
        vehicleApprovalService.getPendingVehicles({ status: 'approved', limit: 1000 }),
        vehicleApprovalService.getPendingVehicles({ status: 'pending_approval', limit: 1000 }),
        vehicleApprovalService.getPendingVehicles({ status: 'rejected', limit: 1000 })
      ]);

      const allVehicles = [
        ...(approvedRes.success ? approvedRes.data.vehicles : []),
        ...(pendingRes.success ? pendingRes.data.vehicles : []),
        ...(rejectedRes.success ? rejectedRes.data.vehicles : [])
      ];

      // Filter vehicles for this specific host
      const hostVehicles = allVehicles
        .filter((vehicle: any) => vehicle.host?.id === hostId)
        .map((vehicle: any) => ({
          id: vehicle.id,
          make: vehicle.vehicle.make,
          model: vehicle.vehicle.model,
          year: vehicle.vehicle.year,
          plateNumber: vehicle.vehicle.plateNumber,
          status: vehicle.status,
          vin: vehicle.vehicle.vin,
          isActive: vehicle.isActive !== undefined ? vehicle.isActive : true,
          suspensionStatus: vehicle.suspensionStatus,
          suspensionReason: vehicle.suspensionReason,
          canHostReactivate: vehicle.canHostReactivate,
          vehicleTypeId: vehicle.vehicle.vehicleTypeId,
          vehicleTypeName: vehicle.vehicle.vehicleTypeName,
          bodyTypeId: vehicle.vehicle.bodyTypeId,
          bodyTypeName: vehicle.vehicle.bodyTypeName
        }));


      // Update the owner's vehicles
      setOwners(prevOwners =>
        prevOwners.map(o =>
          o.id === hostId ? { ...o, vehicles: hostVehicles } : o
        )
      );
    } catch (error) {
      console.error(`❌ Failed to load vehicles for host ${hostId}:`, error);
      toast.error('Failed to load vehicle details');
    } finally {
      setLoadingVehicles(prev => ({ ...prev, [hostId]: false }));
    }
  };

  /**
   * 👥 Load drivers for a specific host
   */
  const loadHostDrivers = async (hostId: string) => {
    // Skip if already loading
    if (loadingDrivers[hostId]) return;

    // Check if already loaded
    const owner = owners.find(o => o.id === hostId);
    if (owner && owner.drivers && owner.drivers.length > 0) return;

    setLoadingDrivers(prev => ({ ...prev, [hostId]: true }));

    try {

      // Fetch ALL drivers and filter by host (similar to how we handle vehicles)
      const response = await driverService.getDrivers({}, 1, 1000); // Large limit to get all drivers

      // Filter drivers that belong to this host
      const hostDrivers = response.drivers
        .filter((driver: any) => {
          // Check if driver has this host as employer
          // The driver object should have employerId or similar field
          return driver.employerId === hostId ||
                 driver.employer_id === hostId ||
                 driver.hostId === hostId ||
                 driver.host_id === hostId ||
                 driver.fleetOwnerId === hostId;
        })
        .map((driver: any) => ({
          id: driver.id,
          firstName: driver.firstName || driver.personalInfo?.firstName || '',
          lastName: driver.lastName || driver.personalInfo?.lastName || '',
          email: driver.email || driver.contactInfo?.email || '',
          phone: driver.phone || driver.contactInfo?.phone || '',
          licenseNumber: driver.licenseNumber || driver.license?.number || '',
          yearsOfExperience: driver.yearsOfExperience || 0,
          isVerified: driver.isVerified || driver.status === 'approved',
          status: driver.status || 'active',
          profilePhotoUrl: driver.profilePicture || driver.profilePhotoUrl,
          totalBookings: driver.totalTrips || driver.totalBookings || 0
        }));


      // Update the owner's drivers
      setOwners(prevOwners =>
        prevOwners.map(o =>
          o.id === hostId ? { ...o, drivers: hostDrivers } : o
        )
      );
    } catch (error) {
      console.error(`❌ Failed to load drivers for host ${hostId}:`, error);
      toast.error('Failed to load driver details');
    } finally {
      setLoadingDrivers(prev => ({ ...prev, [hostId]: false }));
    }
  };

  /**
   * 🛥️ Load yachts for a specific host
   */
  const loadHostYachts = async (hostId: string) => {
    // Skip if already loading
    if (loadingYachts[hostId]) return;

    // Check if already loaded
    const owner = owners.find(o => o.id === hostId);
    if (owner && owner.yachts && owner.yachts.length > 0) return;

    setLoadingYachts(prev => ({ ...prev, [hostId]: true }));

    try {

      // Fetch ALL yachts and filter by host
      const [draftRes, pendingRes, approvedRes, rejectedRes] = await Promise.all([
        yachtApprovalService.getPendingYachts({ status: 'draft', limit: 1000 }),
        yachtApprovalService.getPendingYachts({ status: 'pending_approval', limit: 1000 }),
        yachtApprovalService.getPendingYachts({ status: 'approved', limit: 1000 }),
        yachtApprovalService.getPendingYachts({ status: 'rejected', limit: 1000 })
      ]);

      const allYachts = [
        ...(draftRes.success && draftRes.data?.yachts ? draftRes.data.yachts : []),
        ...(pendingRes.success && pendingRes.data?.yachts ? pendingRes.data.yachts : []),
        ...(approvedRes.success && approvedRes.data?.yachts ? approvedRes.data.yachts : []),
        ...(rejectedRes.success && rejectedRes.data?.yachts ? rejectedRes.data.yachts : [])
      ];

      // Filter yachts for this specific host
      const hostYachts = allYachts
        .filter((yacht: any) => yacht.host?.id === hostId)
        .map((yacht: any) => ({
          id: yacht.id,
          name: yacht.yacht?.name || 'Unnamed Yacht',
          manufacturer: yacht.yacht?.manufacturer,
          model: yacht.yacht?.model,
          year: (yacht.yacht as any)?.year || yacht.yacht?.yearBuilt || 0,
          yacht_type: (yacht.yacht as any)?.yacht_type || yacht.yacht?.type || 'unknown',
          length: yacht.yacht?.length || 0,
          registration_number: (yacht.yacht as any)?.registration_number || yacht.yacht?.registrationNumber,
          status: yacht.status,
          isActive: yacht.isActive !== undefined ? yacht.isActive : true,
          suspensionStatus: yacht.suspensionStatus,
          guest_capacity: (yacht.yacht as any)?.guests ?? (yacht.yacht as any)?.guest_capacity ?? yacht.yacht?.guestCapacity ?? 0
        }));


      // Update the owner's yachts
      setOwners(prevOwners =>
        prevOwners.map(o =>
          o.id === hostId ? { ...o, yachts: hostYachts } : o
        )
      );
    } catch (error) {
      console.error(`❌ Failed to load yachts for host ${hostId}:`, error);
      toast.error('Failed to load yacht details');
    } finally {
      setLoadingYachts(prev => ({ ...prev, [hostId]: false }));
    }
  };

  const handleViewDetails = async (owner: FleetOwner) => {
    // Vehicles already pre-loaded! Load drivers and yachts if needed
    loadHostDrivers(owner.id);
    loadHostYachts(owner.id);

    setSelectedOwnerId(owner.id);
    setDetailsDialog(true);
  };

  const openChangeTypeDialog = (owner: FleetOwner) => {
    setChangeTypeDialog({
      open: true,
      hostId: owner.id,
      hostName: owner.name,
      currentType: owner.type,
    });
    setSelectedNewType(owner.type);
  };

  const handleHostTypeChange = async () => {
    const { hostId, currentType } = changeTypeDialog;
    const newType = selectedNewType;

    if (newType === currentType) {
      toast('Type unchanged');
      setChangeTypeDialog({ ...changeTypeDialog, open: false });
      return;
    }

    try {
      // Optimistically update the UI
      setOwners(prevOwners =>
        prevOwners.map(owner =>
          owner.id === hostId ? { ...owner, type: newType } : owner
        )
      );

      // Close dialog
      setChangeTypeDialog({ ...changeTypeDialog, open: false });

      // Call backend API to update host type
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/hosts/${hostId}/type`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type: newType })
      });

      if (!response.ok) {
        throw new Error('Failed to update host type');
      }

      toast.success(`Host type updated to ${newType === 'fleet_manager' ? 'Fleet Manager' : newType === 'concierge' ? 'Concierge' : 'Individual'}`);

      // Reload overview to update counts
      loadOverview();
    } catch (err) {
      console.error('Failed to update host type:', err);
      toast.error('Failed to update host type');

      // Revert the optimistic update on error
      loadFleetOwners();
    }
  };

  const handleModerationAction = async () => {
    const { action, vehicleId, ownerId } = moderationDialog;

    if (!action || !moderationReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    try {
      let result;
      if (action === 'suspend') {
        result = await suspendVehicle(vehicleId, moderationReason);
      } else if (action === 'blacklist') {
        result = await blacklistVehicle(vehicleId, moderationReason);
      } else if (action === 'reactivate') {
        result = await requestVehicleReactivation(vehicleId, moderationReason);
      }

      if (result?.success) {
        const actionLabels = {
          suspend: 'suspended',
          blacklist: 'blacklisted',
          reactivate: 'reactivation requested for'
        };
        toast.success(`Vehicle ${actionLabels[action]} successfully!`);

        // Update local state
        setOwners(prevOwners =>
          prevOwners.map(owner =>
            owner.id === ownerId
              ? {
                  ...owner,
                  vehicles: owner.vehicles.map(v =>
                    v.id === vehicleId
                      ? {
                          ...v,
                          suspensionStatus: action === 'reactivate' ? 'suspended' : action === 'blacklist' ? 'blacklisted' : 'suspended',
                          isActive: false,
                          suspensionReason: moderationReason,
                        }
                      : v
                  ),
                }
              : owner
          )
        );

        setModerationDialog({
          open: false,
          action: null,
          vehicleId: '',
          vehicleName: '',
          ownerId: '',
        });
        setModerationReason('');

        // Reload data to get fresh state from server
        setTimeout(() => loadFleetOwners(), 1000);
      } else {
        toast.error(result?.error || `Failed to ${action} vehicle`);
      }
    } catch (error) {
      console.error(`Failed to ${action} vehicle:`, error);
      toast.error(`Failed to ${action} vehicle. Please try again.`);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Fleet Owners & Hosts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage individual hosts, fleet managers, and concierge hosts
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => { loadOverview(); loadFleetOwners(); }}>
          Refresh
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab
            label={`All Owners (${totalStats.totalOwners})`}
            value="all"
          />
          <Tab
            label={`Individual Hosts (${totalStats.individualHosts})`}
            value="individual"
            icon={<Person sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            label={`Fleet Managers (${totalStats.fleetManagers})`}
            value="fleet"
            icon={<Business sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            label={`Concierge Hosts (${totalStats.conciergeHosts})`}
            value="concierge"
            icon={<Star sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Hosts
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, mt: 1 }}>
                    {totalStats.totalOwners}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {totalStats.approvedHosts} approved
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <Business />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Vehicles
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, mt: 1 }}>
                    {totalStats.totalVehicles}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {totalStats.approvedVehicles} approved
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                  <DirectionsCar />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Drivers
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, mt: 1 }}>
                    {totalStats.totalDrivers}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {totalStats.verifiedDrivers} verified
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}>
                  <DriveEta />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pending Hosts
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, mt: 1 }}>
                    {totalStats.pendingHosts}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Awaiting approval
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {error}
          </Typography>
          <Typography variant="caption" display="block">
            The backend API is encountering database errors. The backend needs to remove the `compliance_status` column from the SQL queries in the hosts endpoints.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Technical details: The database doesn't have a `compliance_status` column yet, but the backend SQL query is trying to SELECT it.
          </Typography>
        </Alert>
      )}

      {/* Show error state when data fails to load */}
      {!loading && !error && owners.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Warning sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Unable to Load Hosts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            There was an error loading the hosts data from the server.
          </Typography>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => { loadOverview(); loadFleetOwners(); }}>
            Try Again
          </Button>
        </Paper>
      )}

      {/* Filters & Search Bar */}
      {!error && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={4} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="approved">✓ Approved</MenuItem>
                  <MenuItem value="pending">⏳ Pending</MenuItem>
                  <MenuItem value="rejected">✗ Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Vehicle Filter */}
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Vehicles</InputLabel>
                <Select
                  value={hasVehiclesFilter === null ? 'all' : hasVehiclesFilter ? 'has' : 'none'}
                  label="Vehicles"
                  onChange={(e) => {
                    const val = e.target.value;
                    setHasVehiclesFilter(val === 'all' ? null : val === 'has' ? true : false);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="has">🚗 Has Vehicles</MenuItem>
                  <MenuItem value="none">∅ No Vehicles</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Driver Filter */}
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Drivers</InputLabel>
                <Select
                  value={hasDriversFilter === null ? 'all' : hasDriversFilter ? 'has' : 'none'}
                  label="Drivers"
                  onChange={(e) => {
                    const val = e.target.value;
                    setHasDriversFilter(val === 'all' ? null : val === 'has' ? true : false);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="has">👥 Has Drivers</MenuItem>
                  <MenuItem value="none">∅ No Drivers</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters & Count */}
            <Grid item xs={12} md={0.5} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {(statusFilter !== 'all' || hasVehiclesFilter !== null || hasDriversFilter !== null || searchQuery) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setStatusFilter('all');
                    setHasVehiclesFilter(null);
                    setHasDriversFilter(null);
                    setSearchQuery('');
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Clear All
                </Button>
              )}
            </Grid>
          </Grid>

          {/* Results Count */}
          <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing <strong>{filteredOwners.length}</strong> {filteredOwners.length === 1 ? 'host' : 'hosts'}
              {searchQuery && ` matching "${searchQuery}"`}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Owners List */}
      {!error && (
        <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Owner / Host</TableCell>
                  <TableCell>Company</TableCell>
                  {/* Only show Type column on "All Owners" tab */}
                  {activeTab === 'all' && <TableCell align="center">Type</TableCell>}
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Vehicles</TableCell>
                  <TableCell align="center">Yachts</TableCell>
                  <TableCell align="center">Drivers</TableCell>
                  <TableCell align="center">Bookings</TableCell>
                  <TableCell align="center">Earnings</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      {activeTab === 'all' && <TableCell><Skeleton variant="text" /></TableCell>}
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={80} height={32} /></TableCell>
                    </TableRow>
                  ))
                ) : filteredOwners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={activeTab === 'all' ? 10 : 9} align="center" sx={{ py: 8 }}>
                      <Business sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No fleet owners found
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        {searchQuery ? `No results for "${searchQuery}"` : 'No fleet owners or hosts available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOwners.map((owner) => (
                    <React.Fragment key={owner.id}>
                      <TableRow hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Status indicator dot */}
                            <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: owner.status === 'approved' ? 'success.main' : owner.status === 'pending' ? 'warning.main' : 'error.main',
                                  border: '2px solid white',
                                }}
                              />
                            }
                          >
                            <Avatar sx={{ bgcolor: 'primary.light' }}>
                              {owner.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </Badge>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {owner.name}
                            </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {owner.email}
                                </Typography>
                              </Box>
                              {owner.phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                  <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {owner.phone}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {owner.companyName || owner.companyLogoThumbnailUrl ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              {owner.companyLogoThumbnailUrl && (
                                <Avatar
                                  src={owner.companyLogoThumbnailUrl}
                                  variant="rounded"
                                  sx={{ width: 40, height: 40, border: '1px solid', borderColor: 'divider' }}
                                />
                              )}
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {owner.companyName || 'Company'}
                                </Typography>
                                {owner.companyLogoUploadedAt && (
                                  <Typography variant="caption" color="text.secondary">
                                    Verified branding
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        {/* Only show Type column on "All Owners" tab, with clickable badge to change type */}
                        {activeTab === 'all' && (
                          <TableCell align="center">
                            <Chip
                              label={owner.type === 'fleet_manager' ? 'Fleet Manager' : owner.type === 'concierge' ? 'Concierge' : 'Individual'}
                              size="small"
                              color={owner.type === 'fleet_manager' ? 'primary' : owner.type === 'concierge' ? 'secondary' : 'default'}
                              icon={owner.type === 'fleet_manager' ? <Business sx={{ fontSize: 16 }} /> : owner.type === 'concierge' ? <Star sx={{ fontSize: 16 }} /> : <Person sx={{ fontSize: 16 }} />}
                              onClick={() => openChangeTypeDialog(owner)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Chip
                            label={owner.status.charAt(0).toUpperCase() + owner.status.slice(1)}
                            size="small"
                            color={owner.status === 'approved' ? 'success' : owner.status === 'pending' ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {owner.totalVehicles}
                            </Typography>
                            <Typography variant="caption" color="success.main">
                              {owner.approvedVehicles} approved
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {owner.totalYachts || 0}
                            </Typography>
                            <Typography variant="caption" color="success.main">
                              {owner.approvedYachts || 0} approved
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {owner.totalDrivers}
                            </Typography>
                            <Typography variant="caption" color="success.main">
                              {owner.verifiedDrivers} verified
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {owner.totalBookings}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            ₦{owner.totalEarnings.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleViewDetails(owner)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      )}

      {/* Owner Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              {selectedOwner?.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedOwner?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Fleet Owner Details
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOwner && (
            <Box>
              {/* Contact Information */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Contact Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2">{selectedOwner.email}</Typography>
                  </Box>
                </Grid>
                {selectedOwner.phone && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2">{selectedOwner.phone}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Fleet Statistics */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Fleet Statistics
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {(selectedOwner.totalVehicles || 0) + (selectedOwner.totalYachts || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Assets
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.lighter' }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {(selectedOwner.approvedVehicles || 0) + (selectedOwner.approvedYachts || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Approved
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.lighter' }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      {(selectedOwner.pendingVehicles || 0) + (selectedOwner.pendingYachts || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Pending
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.lighter' }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                      {(selectedOwner.rejectedVehicles || 0) + (selectedOwner.rejectedYachts || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rejected
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Vehicles List */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Vehicles ({selectedOwner.totalVehicles})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setAddVehicleDialog({
                    open: true,
                    hostId: selectedOwner.id,
                    hostName: selectedOwner.name
                  })}
                  sx={{ textTransform: 'none' }}
                >
                  Add Vehicle
                </Button>
              </Box>
              {loadingVehicles[selectedOwner.id] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <Stack alignItems="center" spacing={2}>
                    <Box sx={{ position: 'relative' }}>
                      <DirectionsCar sx={{ fontSize: 56, color: 'primary.main', opacity: 0.2 }} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        <Box sx={{
                          width: 70,
                          height: 70,
                          border: '4px solid',
                          borderColor: 'primary.light',
                          borderTopColor: 'primary.main',
                          borderRadius: '50%',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          },
                          animation: 'spin 1s linear infinite'
                        }} />
                      </Box>
                    </Box>
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      Loading vehicle details...
                    </Typography>
                  </Stack>
                </Box>
              ) : selectedOwner.vehicles && selectedOwner.vehicles.length > 0 ? (
                <Grid container spacing={2}>
                  {selectedOwner.vehicles.map((vehicle, index) => (
                    <Grid item xs={12} sm={6} key={vehicle.id}>
                      <Paper
                        sx={{
                          p: 2.5,
                          transition: 'all 0.3s ease',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 4,
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'primary.lighter',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <DirectionsCar sx={{ color: 'primary.main', fontSize: 28 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {vehicle.year} {vehicle.make} {vehicle.model}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              📋 <strong>Plate:</strong> {vehicle.plateNumber}
                            </Typography>
                            {vehicle.vin && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                🔑 <strong>VIN:</strong> {vehicle.vin.substring(0, 10)}...
                              </Typography>
                            )}
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip
                                label={vehicle.status.replace(/_/g, ' ')}
                                size="small"
                                color={
                                  vehicle.status?.toLowerCase() === 'approved' || vehicle.status?.toLowerCase() === 'active' ? 'success' :
                                  vehicle.status?.toLowerCase()?.includes('pending') ? 'warning' :
                                  vehicle.status?.toLowerCase() === 'rejected' || vehicle.status?.toLowerCase() === 'declined' ? 'error' : 'default'
                                }
                                sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Collections />}
                                onClick={() => {
                                  setSelectedVehicle({
                                    id: vehicle.id,
                                    name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`
                                  });
                                  setImageDialog(true);
                                }}
                                sx={{ ml: 'auto', fontSize: '0.75rem' }}
                              >
                                Manage Images
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
                  <DirectionsCar sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
                  <Typography variant="body1" color="text.secondary" fontWeight={500}>
                    No vehicles found
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    This host hasn't added any vehicles yet
                  </Typography>
                </Paper>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Drivers List */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Drivers ({selectedOwner.totalDrivers})
              </Typography>
              {loadingDrivers[selectedOwner.id] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <Stack alignItems="center" spacing={2}>
                    <Box sx={{ position: 'relative' }}>
                      <DriveEta sx={{ fontSize: 56, color: 'secondary.main', opacity: 0.2 }} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        <Box sx={{
                          width: 70,
                          height: 70,
                          border: '4px solid',
                          borderColor: 'secondary.light',
                          borderTopColor: 'secondary.main',
                          borderRadius: '50%',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          },
                          animation: 'spin 1s linear infinite'
                        }} />
                      </Box>
                    </Box>
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      Loading driver details...
                    </Typography>
                  </Stack>
                </Box>
              ) : selectedOwner.drivers && selectedOwner.drivers.length > 0 ? (
                <Grid container spacing={2}>
                  {selectedOwner.drivers.map((driver, index) => (
                    <Grid item xs={12} sm={6} key={driver.id}>
                      <Paper
                        sx={{
                          p: 2.5,
                          transition: 'all 0.3s ease',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 4,
                            borderColor: 'secondary.main'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar
                            src={driver.profilePhotoUrl}
                            sx={{
                              width: 52,
                              height: 52,
                              border: '2px solid',
                              borderColor: driver.isVerified ? 'success.main' : 'divider'
                            }}
                          >
                            {driver.firstName.charAt(0)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {driver.firstName} {driver.lastName}
                              </Typography>
                              {driver.isVerified && (
                                <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              ✉️ {driver.email || 'No email'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              📞 {driver.phone}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                              🪪 License: {driver.licenseNumber}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={driver.status}
                                size="small"
                                color={driver.status === 'active' ? 'success' : 'default'}
                                sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                              />
                              <Chip
                                label={`${driver.yearsOfExperience} yrs exp`}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={`${driver.totalBookings} bookings`}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
                  <DriveEta sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
                  <Typography variant="body1" color="text.secondary" fontWeight={500}>
                    No drivers found
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    This host hasn't added any verified drivers yet
                  </Typography>
                </Paper>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Yachts List */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Yachts ({selectedOwner.totalYachts || 0})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  startIcon={<SailingOutlined />}
                  onClick={() => setAddYachtDialog({
                    open: true,
                    hostId: selectedOwner.id,
                    hostName: selectedOwner.name
                  })}
                  sx={{ textTransform: 'none' }}
                >
                  Add Yacht
                </Button>
              </Box>
              {loadingYachts[selectedOwner.id] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <Stack alignItems="center" spacing={2}>
                    <Box sx={{ position: 'relative' }}>
                      <SailingOutlined sx={{ fontSize: 56, color: 'info.main', opacity: 0.2 }} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        <Box sx={{
                          width: 70,
                          height: 70,
                          border: '4px solid',
                          borderColor: 'info.light',
                          borderTopColor: 'info.main',
                          borderRadius: '50%',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          },
                          animation: 'spin 1s linear infinite'
                        }} />
                      </Box>
                    </Box>
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      Loading yacht details...
                    </Typography>
                  </Stack>
                </Box>
              ) : selectedOwner.yachts && selectedOwner.yachts.length > 0 ? (
                <Grid container spacing={2}>
                  {selectedOwner.yachts.map((yacht, index) => (
                    <Grid item xs={12} sm={6} key={yacht.id}>
                      <Paper
                        sx={{
                          p: 2.5,
                          transition: 'all 0.3s ease',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 4,
                            borderColor: 'info.main'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              bgcolor: 'info.lighter',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <SailingOutlined sx={{ color: 'info.main', fontSize: 28 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {yacht.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              {yacht.year && `${yacht.year} • `}{yacht.yacht_type?.replace(/_/g, ' ')}{yacht.length && ` • ${yacht.length}ft`}
                            </Typography>
                            {yacht.registration_number && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                📋 <strong>Reg:</strong> {yacht.registration_number}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip
                                label={yacht.status.replace(/_/g, ' ')}
                                size="small"
                                color={
                                  yacht.status?.toLowerCase() === 'approved' || yacht.status?.toLowerCase() === 'active' ? 'success' :
                                  yacht.status?.toLowerCase()?.includes('pending') ? 'warning' :
                                  yacht.status?.toLowerCase() === 'rejected' || yacht.status?.toLowerCase() === 'declined' ? 'error' : 'default'
                                }
                                sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Collections />}
                                onClick={() => {
                                  setSelectedYacht({
                                    id: yacht.id,
                                    name: yacht.name
                                  });
                                  setYachtImageDialog(true);
                                }}
                                sx={{ ml: 'auto', fontSize: '0.75rem' }}
                              >
                                Manage Images
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
                  <SailingOutlined sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
                  <Typography variant="body1" color="text.secondary" fontWeight={500}>
                    No yachts found
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    This host hasn't added any yachts yet
                  </Typography>
                </Paper>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Company Branding Section */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Company Branding
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Business />}
                  onClick={() => {
                    setCompanyBrandingDialog({
                      open: true,
                      hostId: selectedOwner.id,
                      hostName: selectedOwner.name,
                      currentName: selectedOwner.companyName,
                      currentLogoUrl: selectedOwner.companyLogoUrl,
                    });
                    setCompanyName(selectedOwner.companyName || '');
                    setCompanyLogoPreview(selectedOwner.companyLogoUrl || '');
                  }}
                >
                  Edit Branding
                </Button>
              </Box>
              <Paper sx={{ p: 2.5, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Company Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedOwner.companyName || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Company Logo
                    </Typography>
                    {(selectedOwner.companyLogoThumbnailUrl || selectedOwner.companyLogoUrl) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={selectedOwner.companyLogoThumbnailUrl || selectedOwner.companyLogoUrl}
                          variant="rounded"
                          sx={{ width: 60, height: 60, border: '1px solid', borderColor: 'divider' }}
                        />
                        <Box>
                          <Chip label="Uploaded" size="small" color="success" sx={{ mb: 0.5 }} />
                          {selectedOwner.companyLogoUploadedAt && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Last updated: {new Date(selectedOwner.companyLogoUploadedAt).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No logo uploaded
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Image Management Dialog */}
      {selectedVehicle && (
        <VehicleImageManagementDialog
          open={imageDialog}
          onClose={() => {
            setImageDialog(false);
            setSelectedVehicle(null);
          }}
          vehicleId={selectedVehicle.id}
          vehicleName={selectedVehicle.name}
        />
      )}

      {/* Yacht Image Management Dialog */}
      {selectedYacht && (
        <YachtImageManagementDialog
          open={yachtImageDialog}
          onClose={() => {
            setYachtImageDialog(false);
            setSelectedYacht(null);
          }}
          yachtId={selectedYacht.id}
          yachtName={selectedYacht.name}
        />
      )}

      {/* Change Type Dialog */}
      <Dialog
        open={changeTypeDialog.open}
        onClose={() => setChangeTypeDialog({ ...changeTypeDialog, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Change Host Type
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Change <strong>{changeTypeDialog.hostName}</strong> to:
            </Typography>
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Paper
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selectedNewType === 'individual' ? 'primary.main' : 'transparent',
                  bgcolor: selectedNewType === 'individual' ? 'primary.50' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => setSelectedNewType('individual')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Individual Host</Typography>
                    <Typography variant="caption" color="text.secondary">1-2 vehicles, self-managed</Typography>
                  </Box>
                </Box>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selectedNewType === 'fleet_manager' ? 'primary.main' : 'transparent',
                  bgcolor: selectedNewType === 'fleet_manager' ? 'primary.50' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => setSelectedNewType('fleet_manager')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Fleet Manager</Typography>
                    <Typography variant="caption" color="text.secondary">Multiple vehicles, business operations</Typography>
                  </Box>
                </Box>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selectedNewType === 'concierge' ? 'secondary.main' : 'transparent',
                  bgcolor: selectedNewType === 'concierge' ? 'secondary.50' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => setSelectedNewType('concierge')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Star />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Concierge Host</Typography>
                    <Typography variant="caption" color="text.secondary">Premium service, white glove experience</Typography>
                  </Box>
                </Box>
              </Paper>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeTypeDialog({ ...changeTypeDialog, open: false })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleHostTypeChange}
          >
            Change Type
          </Button>
        </DialogActions>
      </Dialog>

      {/* Moderation Action Dialog */}
      <Dialog
        open={moderationDialog.open}
        onClose={() => setModerationDialog({
          open: false,
          action: null,
          vehicleId: '',
          vehicleName: '',
          ownerId: '',
        })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {moderationDialog.action === 'suspend' && '⚠️ Suspend Vehicle'}
          {moderationDialog.action === 'blacklist' && '🚫 Blacklist Vehicle'}
          {moderationDialog.action === 'reactivate' && '✅ Request Reactivation'}
        </DialogTitle>
        <DialogContent>
          <Alert
            severity={
              moderationDialog.action === 'blacklist' ? 'error' :
              moderationDialog.action === 'suspend' ? 'warning' : 'info'
            }
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight={500}>
              {moderationDialog.vehicleName}
            </Typography>
            <Typography variant="caption">
              {moderationDialog.action === 'suspend' && 'Vehicle will be hidden from renters. Can be reactivated by supervisor approval.'}
              {moderationDialog.action === 'blacklist' && '⚠️ PERMANENT ACTION: Vehicle will be permanently blocked. Cannot be undone!'}
              {moderationDialog.action === 'reactivate' && 'Request will be sent to supervisor for approval.'}
            </Typography>
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={4}
            label={moderationDialog.action === 'reactivate' ? 'Justification (Required)' : 'Reason (Required)'}
            value={moderationReason}
            onChange={(e) => setModerationReason(e.target.value)}
            placeholder={
              moderationDialog.action === 'suspend' ? 'e.g., Vehicle documents expired' :
              moderationDialog.action === 'blacklist' ? 'e.g., Fraudulent activity, fake documents' :
              'e.g., Documents have been renewed and verified'
            }
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setModerationDialog({
                open: false,
                action: null,
                vehicleId: '',
                vehicleName: '',
                ownerId: '',
              });
              setModerationReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={
              moderationDialog.action === 'blacklist' ? 'error' :
              moderationDialog.action === 'suspend' ? 'warning' : 'success'
            }
            onClick={handleModerationAction}
            disabled={!moderationReason.trim()}
          >
            {moderationDialog.action === 'suspend' && 'Suspend'}
            {moderationDialog.action === 'blacklist' && 'Blacklist'}
            {moderationDialog.action === 'reactivate' && 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company Branding Dialog */}
      <Dialog
        open={companyBrandingDialog.open}
        onClose={() => {
          setCompanyBrandingDialog({ open: false, hostId: '', hostName: '' });
          setCompanyName('');
          setCompanyLogoFile(null);
          setCompanyLogoPreview('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business />
            Edit Company Branding - {companyBrandingDialog.hostName}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Company Name */}
            <TextField
              fullWidth
              label="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g., ABC Transport Ltd"
              helperText="Enter the company or business name"
              sx={{ mb: 3 }}
            />

            {/* Logo Upload */}
            <Typography variant="subtitle2" gutterBottom>
              Company Logo
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Square image recommended. Max 5MB. JPG, PNG, or WebP.
            </Typography>

            {companyLogoPreview && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Avatar
                  src={companyLogoPreview}
                  variant="rounded"
                  sx={{ width: 120, height: 120, border: '2px solid', borderColor: 'divider' }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
              >
                {companyLogoFile ? 'Change Logo' : 'Upload Logo'}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File size must be less than 5MB');
                        return;
                      }
                      setCompanyLogoFile(file);
                      setCompanyLogoPreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </Button>

              {(companyLogoFile || companyBrandingDialog.currentLogoUrl) && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setCompanyLogoFile(null);
                    setCompanyLogoPreview('');
                  }}
                >
                  <Delete />
                </Button>
              )}
            </Box>

            {companyLogoFile && (
              <Alert severity="info" sx={{ mb: 2 }}>
                New logo selected: {companyLogoFile.name}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCompanyBrandingDialog({ open: false, hostId: '', hostName: '' });
              setCompanyName('');
              setCompanyLogoFile(null);
              setCompanyLogoPreview('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!companyName.trim() && !companyLogoFile) {
                toast.error('Please enter a company name or upload a logo');
                return;
              }

              setUploadingLogo(true);
              try {
                const formData = new FormData();
                if (companyName.trim()) {
                  formData.append('companyName', companyName.trim());
                }
                if (companyLogoFile) {
                  // Compress logo before uploading
                  toast.loading('Compressing logo...', { id: 'compress' });
                  const compressedBlob = await compressLogoImage(companyLogoFile);
                  toast.dismiss('compress');

                  // Create a new File from the Blob with original filename
                  const compressedFile = new File(
                    [compressedBlob],
                    companyLogoFile.name,
                    { type: 'image/jpeg' }
                  );

                  formData.append('logo', compressedFile);
                }

                // TODO: Backend needs to create this endpoint
                const response = await fetch(
                  `${getApiBaseUrl()}/api/v1/admin/hosts/${companyBrandingDialog.hostId}/company-branding`,
                  {
                    method: 'PATCH',
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    },
                    body: formData,
                  }
                );

                if (!response.ok) {
                  const errorData = await response.json().catch(() => ({}));
                  console.error('❌ Update failed:', errorData);
                  throw new Error(errorData.message || 'Failed to update company branding');
                }

                const result = await response.json();

                toast.success('Company branding updated successfully');
                setCompanyBrandingDialog({ open: false, hostId: '', hostName: '' });
                setCompanyName('');
                setCompanyLogoFile(null);
                setCompanyLogoPreview('');

                // Reload the entire page data to show updated branding
                await loadFleetOwners();
              } catch (error: any) {
                console.error('Error updating company branding:', error);
                toast.error(error.message || 'Failed to update company branding');
              } finally {
                setUploadingLogo(false);
              }
            }}
            disabled={uploadingLogo}
          >
            {uploadingLogo ? 'Updating...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Vehicle Dialog */}
      <AddVehicleForHostDialog
        open={addVehicleDialog.open}
        onClose={() => setAddVehicleDialog({ open: false, hostId: '', hostName: '' })}
        hostId={addVehicleDialog.hostId}
        hostName={addVehicleDialog.hostName}
        onSuccess={() => {
          // Reload the host's vehicles after successful creation
          if (addVehicleDialog.hostId) {
            loadHostVehicles(addVehicleDialog.hostId);
          }
          // Also reload the entire fleet owners list to update counts
          loadFleetOwners();
        }}
      />

      {/* Add Yacht Dialog */}
      <AddYachtForHostDialog
        open={addYachtDialog.open}
        onClose={() => setAddYachtDialog({ open: false, hostId: '', hostName: '' })}
        hostId={addYachtDialog.hostId}
        hostName={addYachtDialog.hostName}
        onSuccess={() => {
          // Reload the host's yachts after successful creation
          if (addYachtDialog.hostId) {
            loadHostYachts(addYachtDialog.hostId);
          }
          // Also reload the entire fleet owners list to update counts
          loadFleetOwners();
        }}
      />
    </Box>
  );
};

export default FleetOwnersPage;
